import React, {useCallback, useEffect, useRef, useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@material-ui/core";
import {
  WifiOff,
  Warning,
  Block,
  CloudOff,
  SignalCellularConnectedNoInternet2Bar,
} from "@material-ui/icons";
import {useNetworkStatus} from "store/network-status/network-status.store";
import clsx from "clsx";
import useStyles from "./styles";

// Thresholds for the Network Information API. Values chosen to flag
// connections where interactive actions will feel noticeably laggy.
const SLOW_RTT_MS = 500;
const SLOW_DOWNLINK_MBPS = 0.5;

type NetInfoConnection = {
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
  addEventListener?: (type: "change", cb: () => void) => void;
  removeEventListener?: (type: "change", cb: () => void) => void;
};

const getNetInfo = (): NetInfoConnection | undefined =>
  ((navigator as unknown) as {connection?: NetInfoConnection}).connection;

const isSlowByNetInfo = (): boolean => {
  const conn = getNetInfo();
  if (!conn) return false;
  if (conn.saveData) return true;
  if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") {
    return true;
  }
  if (typeof conn.rtt === "number" && conn.rtt > 0 && conn.rtt >= SLOW_RTT_MS) {
    return true;
  }
  if (
    typeof conn.downlink === "number" &&
    conn.downlink > 0 &&
    conn.downlink < SLOW_DOWNLINK_MBPS
  ) {
    return true;
  }
  return false;
};

// Periodic recheck only runs while the app is already in a degraded state
// (unstable / services-unavailable) to catch recovery. When online we trust
// real API calls as the signal — `reportFailedRequest` flips the status
// when they start failing, at which point this probe kicks in.
const HEALTH_CHECK_INTERVAL = 300_000;
const OFFLINE_RECHECK_INTERVAL = 15_000;

export const PROXY_ALLOWLIST_HOSTS = [
  // App + proxy + FFAIR backend
  "app.ffair.show",
  "proxy.ffair.show",
  "api.ffair.io",
  "api.ffair.show",
  "public-file.ffair.show",
  // Third parties (not tunneled through proxy)
  "accounts.google.com",
  "fonts.googleapis.com",
  "fonts.gstatic.com",
  "js.stripe.com",
  "api.stripe.com",
  "m.stripe.network",
  "static.userguiding.com",
  "widget.frill.co",
  "browser-intake-datadoghq.eu",
  "openreplay.ffair.io",
];

export const DEFAULT_ALLOWLIST_HOSTS = [
  // App + FFAIR backend
  "app.ffair.io",
  "api.ffair.io",
  "public-file.ffair.show",
  // Firebase services
  "us-central1-ffair-live.cloudfunctions.net",
  "firestore.googleapis.com",
  "firebasestorage.googleapis.com",
  "identitytoolkit.googleapis.com",
  "securetoken.googleapis.com",
  "firebaseinstallations.googleapis.com",
  "www.googleapis.com",
  // Third parties
  "accounts.google.com",
  "fonts.googleapis.com",
  "fonts.gstatic.com",
  "js.stripe.com",
  "api.stripe.com",
  "m.stripe.network",
  "static.userguiding.com",
  "widget.frill.co",
  "browser-intake-datadoghq.eu",
  "openreplay.ffair.io",
];

const getAllowlistHosts = (): string[] =>
  window.location.hostname.endsWith("ffair.show")
    ? PROXY_ALLOWLIST_HOSTS
    : DEFAULT_ALLOWLIST_HOSTS;

const isEmbeddedInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws — that means we are in an iframe
    return true;
  }
};

const checkCookiesEnabled = (): boolean => {
  // Third-party cookie restrictions inside iframes (Safari ITP, Chrome's
  // third-party cookie blocking) make this check unreliable, so skip it when
  // embedded — the parent page owns the browsing context.
  if (isEmbeddedInIframe()) return true;

  if (!navigator.cookieEnabled) return false;

  // Double-check by trying to write/read a test cookie
  try {
    document.cookie = "__cookie_test=1; SameSite=Strict";
    const hasCookie = document.cookie.indexOf("__cookie_test=") !== -1;
    // Clean up
    document.cookie =
      "__cookie_test=1; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict";
    return hasCookie;
  } catch {
    return false;
  }
};

const NetworkStatusMonitor: React.FC = () => {
  const classes = useStyles();
  const status = useNetworkStatus((s) => s.status);
  const serviceHealth = useNetworkStatus((s) => s.serviceHealth);
  const setStatus = useNetworkStatus((s) => s.setStatus);
  const checkConnection = useNetworkStatus((s) => s.checkConnection);
  const checkServices = useNetworkStatus((s) => s.checkServices);

  const connectionQuality = useNetworkStatus((s) => s.connectionQuality);
  const setConnectionQuality = useNetworkStatus((s) => s.setConnectionQuality);
  const [cookiesEnabled, setCookiesEnabled] = useState(() =>
    checkCookiesEnabled()
  );
  const [isChecking, setIsChecking] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [slowWarningDismissed, setSlowWarningDismissed] = useState(false);
  const [allowlistExpanded, setAllowlistExpanded] = useState(false);
  const healthCheckRef = useRef<ReturnType<typeof setInterval>>();
  const offlineRecheckRef = useRef<ReturnType<typeof setInterval>>();

  // Initial check on mount — checks internet first, then services
  useEffect(() => {
    checkConnection();
  }, []);

  // Browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      checkConnection();
    };

    const handleOffline = () => {
      setStatus("offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [setStatus, checkConnection]);

  // Periodic health check only while degraded — skip when online (real
  // requests are the monitor) and when offline (handled separately below).
  useEffect(() => {
    if (status !== "unstable" && status !== "services-unavailable") {
      clearInterval(healthCheckRef.current);
      return;
    }

    healthCheckRef.current = setInterval(() => {
      checkConnection();
    }, HEALTH_CHECK_INTERVAL);

    return () => clearInterval(healthCheckRef.current);
  }, [status, checkConnection]);

  // Auto-recheck when offline
  useEffect(() => {
    if (status !== "offline") {
      clearInterval(offlineRecheckRef.current);
      return;
    }

    offlineRecheckRef.current = setInterval(() => {
      checkConnection();
    }, OFFLINE_RECHECK_INTERVAL);

    return () => clearInterval(offlineRecheckRef.current);
  }, [status, checkConnection]);

  // When status changes: reset dismissed, diagnose on unstable
  useEffect(() => {
    if (status === "online") {
      setWarningDismissed(false);
    }
    if (status === "unstable") {
      // noinspection JSIgnoredPromiseFromResultOf
      checkServices().catch(Function.prototype as () => void);
    }
  }, [status, checkServices]);

  // Connection quality via the Network Information API. Supported in
  // Chromium-based browsers; Safari/Firefox stay "fast" forever, which is
  // fine — we just won't show a slow-connection banner there.
  useEffect(() => {
    const conn = getNetInfo();
    if (!conn) return;

    const refresh = () => {
      const next: "fast" | "slow" = isSlowByNetInfo() ? "slow" : "fast";
      setConnectionQuality(next);
      if (next === "fast") setSlowWarningDismissed(false);
    };
    refresh();

    conn.addEventListener?.("change", refresh);
    return () => conn.removeEventListener?.("change", refresh);
  }, [setConnectionQuality]);

  const handleRetry = useCallback(async () => {
    setIsChecking(true);
    await checkConnection();
    setIsChecking(false);
  }, [checkConnection]);

  // Cookies disabled — blocking modal, highest priority
  if (!cookiesEnabled) {
    return (
      <Box className={classes.modalOverlay}>
        <Paper className={classes.modalPaper} elevation={0}>
          <Block className={classes.cookieIcon} />
          <Typography className={classes.modalTitle}>
            Cookies Are Disabled
          </Typography>
          <Typography className={classes.modalSubtitle}>
            This application requires cookies to function properly.
            <br />
            Please enable cookies in your browser settings and click the button
            below.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            className={classes.retryButton}
            onClick={() => setCookiesEnabled(checkCookiesEnabled())}
          >
            Check Again
          </Button>
        </Paper>
      </Box>
    );
  }

  // Services unreachable — internet works but API/Firebase are down
  if (status === "services-unavailable") {
    return (
      <Box className={classes.modalOverlay}>
        <Paper className={classes.modalPaper} elevation={0}>
          <CloudOff className={classes.serviceIcon} />
          <Typography className={classes.modalTitle}>
            Services Unavailable
          </Typography>
          <Typography className={classes.modalSubtitle}>
            Your internet connection is working, but one or more of our services
            are unreachable.
          </Typography>
          <Box className={classes.serviceList}>
            {([
              ["FFAIR API", serviceHealth.api],
              ["Firebase Auth", serviceHealth.auth],
              ["Firestore", serviceHealth.firebase],
              ["Cloud Functions", serviceHealth.functions],
              ["Firebase Storage", serviceHealth.firebaseStorage],
              ["Notifications", serviceHealth.notifications],
              ["File Upload", serviceHealth.storage],
              ["Stripe", serviceHealth.stripe],
            ] as const).map(([name, ok]) => (
              <Box key={name} className={classes.serviceRow}>
                <span
                  className={clsx(
                    classes.serviceDot,
                    ok ? classes.serviceDotOk : classes.serviceDotFail
                  )}
                />
                {name} — {ok ? "Connected" : "Unavailable"}
              </Box>
            ))}
          </Box>
          <Button
            variant="contained"
            color="primary"
            className={classes.retryButton}
            onClick={async () => {
              setIsChecking(true);
              const health = await checkServices();
              setIsChecking(false);
              if (
                health.api &&
                health.firebase &&
                health.auth &&
                health.functions &&
                health.firebaseStorage &&
                health.notifications &&
                health.storage &&
                health.stripe
              ) {
                window.location.reload();
              }
            }}
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <CircularProgress
                  size={18}
                  color="inherit"
                  className={classes.checkingProgress}
                />
                Checking...
              </>
            ) : (
              "Try Again"
            )}
          </Button>
          <Box className={classes.vpnHint}>
            <Typography className={classes.vpnHintTitle}>
              On a corporate VPN or private network?
            </Typography>
            <Typography className={classes.vpnHintText}>
              Ask your IT / network administrator to allow the hosts below in
              the corporate firewall or proxy, and make sure browser extensions
              (ad / tracker blockers) are not blocking them:
            </Typography>
            <Button
              size="small"
              className={classes.vpnToggle}
              onClick={() => setAllowlistExpanded((v) => !v)}
            >
              {allowlistExpanded ? "Hide hosts" : "Show hosts"}
            </Button>
            {allowlistExpanded && (
              <Box className={classes.vpnList}>
                {getAllowlistHosts().map((host) => (
                  <code key={host} className={classes.vpnHost}>
                    {host}
                  </code>
                ))}
              </Box>
            )}
          </Box>
          <Typography className={classes.supportText}>
            If the problem persists, please contact support.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Unstable connection warning banner
  if (status === "unstable" && !warningDismissed) {
    const issues: string[] = [];
    if (!serviceHealth.api) issues.push("FFAIR API");
    if (!serviceHealth.auth) issues.push("Firebase Auth");
    if (!serviceHealth.firebase) issues.push("Firestore");
    if (!serviceHealth.functions) issues.push("Cloud Functions");
    if (!serviceHealth.firebaseStorage) issues.push("Firebase Storage");
    if (!serviceHealth.notifications) issues.push("Notifications");
    if (!serviceHealth.storage) issues.push("File Upload");
    if (!serviceHealth.stripe) issues.push("Stripe");
    const hasServiceIssues = issues.length > 0;

    const bannerServices = [
      ["API", serviceHealth.api],
      ["Auth", serviceHealth.auth],
      ["Firestore", serviceHealth.firebase],
      ["Functions", serviceHealth.functions],
      ["FB Storage", serviceHealth.firebaseStorage],
      ["Notifications", serviceHealth.notifications],
      ["File Upload", serviceHealth.storage],
      ["Stripe", serviceHealth.stripe],
    ] as const;

    return (
      <Box className={classes.warningBanner}>
        <Warning className={classes.warningIcon} />
        <Box className={classes.warningText}>
          <span>
            {hasServiceIssues
              ? `Connection issues detected: ${issues.join(", ")} unavailable.`
              : "Unstable internet connection. Some features may not work properly."}
          </span>
          {hasServiceIssues && (
            <>
              <span className={classes.bannerSeparator} />
              {bannerServices.map(([name, ok]) => (
                <span key={name} className={classes.bannerServiceItem}>
                  <span
                    className={clsx(
                      classes.bannerServiceDot,
                      ok ? classes.bannerServiceOk : classes.bannerServiceFail
                    )}
                  />
                  {name}
                </span>
              ))}
            </>
          )}
        </Box>
        <Button
          className={classes.dismissButton}
          onClick={() => setWarningDismissed(true)}
          size="small"
        >
          Dismiss
        </Button>
      </Box>
    );
  }

  // Slow connection — non-blocking informational banner. Only shown when
  // the app is otherwise "online", to avoid stacking banners.
  if (
    status === "online" &&
    connectionQuality === "slow" &&
    !slowWarningDismissed
  ) {
    return (
      <Box className={clsx(classes.warningBanner, classes.slowBanner)}>
        <SignalCellularConnectedNoInternet2Bar
          className={classes.warningIcon}
        />
        <Box className={classes.warningText}>
          <span>
            Your internet connection appears slow or unstable. Some features may
            load slowly.
          </span>
        </Box>
        <Button
          className={classes.dismissButton}
          onClick={() => setSlowWarningDismissed(true)}
          size="small"
        >
          Dismiss
        </Button>
      </Box>
    );
  }

  // Offline blocking modal
  if (status === "offline") {
    return (
      <Box className={classes.modalOverlay}>
        <Paper className={classes.modalPaper} elevation={0}>
          <WifiOff className={classes.offlineIcon} />
          <Typography className={classes.modalTitle}>
            No Internet Connection
          </Typography>
          <Typography className={classes.modalSubtitle}>
            Please check your network settings and try again.
            <br />
            The application requires an internet connection to work.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            className={classes.retryButton}
            onClick={handleRetry}
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <CircularProgress
                  size={18}
                  color="inherit"
                  className={classes.checkingProgress}
                />
                Checking...
              </>
            ) : (
              "Check Connection"
            )}
          </Button>
        </Paper>
      </Box>
    );
  }

  return null;
};

export default NetworkStatusMonitor;
